import { defineStore } from "pinia";
import { ViewListIcon, CubeIcon } from "@heroicons/vue/outline";
import { http } from "@/composable/httpService";
import axios from "axios";
import { useHandleErrorStatus } from "@/composable/useHandleErrorStatus";
import eventBus from "@/plugins/eventBus";

export const useFileManagerStore = defineStore("fileManagerStore", {
  state: () => ({
    toggles: [
      {
        name: "List",
        icon: ViewListIcon,
        selected: false,
      },
      {
        name: "Card",
        icon: CubeIcon,
        selected: true,
      },
    ],
    tabs: [
      {
        name: "All",
        value: "all",
        current: true,
      },
      {
        name: "Docs",
        value: "docs",
        current: false,
      },
      {
        name: "Multimedia",
        value: "multimedia",
        current: false,
      },
      {
        name: "Assigned",
        value: "assigned",
        current: false,
      },
      {
        name: "Others",
        value: "others",
        current: false,
      },
      {
        name: "Trash",
        value: "trash",
        current: false,
      },
    ],
    sorts: [
      { value: "desc", name: "Newest - Oldest" },
      { value: "asc", name: "Oldest - Newest" },
    ],
    files: [],
    folders: [],
    selectedFolder: null,
    selectedFolderId: null,
    selectedTab: "all",
    selectedToggle: "Card",
    selectedSort: "desc",
    search: "",
    selectedFile: null,
    isClickEdit: false,
    isViewSelectedFile: false,
    isMultipleSelect: false,
    assignableFiles: [],
    isAttachToUnits: false,
    selectedUnitIds: [],
    units: [],
    isSelectAll: false,
    loadingFiles: false,
    hasData: true,
    isAssignToUsers: false,
    isMovingFiles: false,
    selectedUsers: [],
    selectedStores: [],
    selectedCompanies: [],
    isAssigned: false,
    assignedError: null,
    assignLoading: false,
    isLoadingFile: false,
    loadingAssignedUsers: false,
    assignedUsers: [],
    assignedStores: [],
    assignedCompanies: [],
    openRenameDialog: false,
    loadingRename: false,
    errorRename: null,
    isLoadingAttach: false,
    loadingUnitsRelatedLinks: true,
    unitsRelatedLinks: [],
    errorAttachUnit: null,
    loadingDelete: false,
    loadingDuplicate: false,
    selectedTrash: [],
    currentFolder: null,
    lastPage: 1,
  }),
  getters: {
    filteredTabs() {
      return this.tabs.map((tab) => {
        tab.current = tab.value === this.selectedTab;
        return tab;
      });
    },
    filteredToggles() {
      return this.toggles.map((toggle) => {
        toggle.selected = toggle.name === this.selectedToggle;
        return toggle;
      });
    },
    getSelectedSort() {
      return this.sorts.find((sort) => sort.value === this.selectedSort).name;
    },
    getSelectedUnits() {
      return this.units.filter((unit) =>
        this.selectedUnitIds.includes(unit.id)
      );
    },
    assignableFilesPayload() {
      return {
        user_ids: this.selectedUsers,
        store_ids: this.selectedStores,
        company_ids: this.selectedCompanies,
        file_ids: this.assignableFiles.map((file) => file.id),
      };
    },
    moveFilesPayload() {
      return {
        selected_folder: this.selectedFolder,
        file_ids: this.assignableFiles.map((file) => file.id),
      };
    },
    attachToUnitsPayload() {
      return {
        fileIds: this.assignableFiles.map((file) => file.id),
        unitIds: this.selectedUnitIds,
      };
    },
    deletedFilesPayload() {
      return {
        fileIds: this.assignableFiles.map((file) => file.id),
      };
    },
    checkIfUnAuthorized() {
      return this.assignableFiles.some((file) => !file.is_author);
    },
    restoreFilesPayload() {
      return this.selectedTrash.map((file) => file.id);
    },
    getFolders() {
      return this.folders;
    },
  },
  actions: {
    setAssignableFileIds(file) {
      let isExistId = this.assignableFiles.some(
        (assignedFile) => assignedFile.id === file.id
      );

      if (!isExistId) {
        this.assignableFiles.push(file);
      }

      return isExistId;
    },
    setTrashFiles(file) {
      let isExistId = this.selectedTrash.some(
        (assignedFile) => assignedFile.id === file.id
      );

      if (!isExistId) {
        this.selectedTrash.push(file);
      }

      return isExistId;
    },
    removeSelectedFile(file) {
      this.assignableFiles = this.assignableFiles.filter(
        (assignedFile) => assignedFile.id !== file.id
      );
    },
    removeSelectedTrashFile(file) {
      this.selectedTrash = this.selectedTrash.filter(
        (assignedFile) => assignedFile.id !== file.id
      );
    },
    setSelectedUnitIds(id) {
      let isExistId = this.selectedUnitIds.some(
        (assignedId) => assignedId === id
      );

      if (isExistId) {
        this.selectedUnitIds = this.selectedUnitIds.filter(
          (assignedId) => assignedId !== id
        );
      } else {
        this.selectedUnitIds.push(id);
      }
    },
    setSelectAllUnitIds() {
      this.isSelectAll = !this.isSelectAll;
      if (this.isSelectAll) {
        this.selectedUnitIds = this.units.map((unit) => unit.id);
      } else {
        this.selectedUnitIds = [];
      }
    },
    // get all paginated files
    async fetchFiles(params, refresh = false) {
      if (this.loadingFiles || (params.page > this.lastPage && !refresh)) {
        return;
      }

      this.loadingFiles = true;

      await http()
        .get("v3/file-manager/files", { params })
        .then((response) => {
          if (response.data.data.length === 0) {
            this.hasData = false;
          }
          if (params.page > 1) {
            this.files.push(...response.data.data);
          } else {
            this.files = response.data.data;
          }

          this.tabs = response.data.tabs;
          this.folders = response.data.folders;
          this.currentFolder = response.data.currentFolder;
          this.foldersParent = response.data.foldersParent;
          this.lastPage = response.data.meta.last_page;
          this.loadingFiles = false;
        })
        .catch((error) => {
          useHandleErrorStatus(error);
        })
        .finally(() => {
          this.loadingFiles = false;
        });
    },
    // get file information
    async fetchFile(id) {
      this.isLoadingFile = true;
      await http()
        .get(`v3/file-manager/file/${id}`)
        .then((response) => {
          this.selectedFile = response.data.data;
          this.isViewSelectedFile = true;
        })
        .catch(() => {
          this.isViewSelectedFile = false;
        })
        .finally(() => {
          this.isLoadingFile = false;
        });
    },
    // assigned users to a file
    async assignedFiles() {
      this.assignLoading = true;
      await http()
        .post("v3/file-manager/assign-files", this.assignableFilesPayload)
        .then(() => {
          eventBus.$emit(
            "CREATE_NOTIFICATION",
            "Assigned successfully",
            "success",
            6500
          );
          if (
            this.assignableFilesPayload.file_ids.length === 1 &&
            this.selectedFile
          ) {
            this.fetchAssignedUsers(this.selectedFile.id);
          }
        })
        .catch((error) => {
          const { data } = useHandleErrorStatus(error);
          this.assignedError = data.errors;
        })
        .finally(() => {
          this.assignLoading = false;
        });
    },
    async moveFiles() {
      await http()
        .post("v3/file-manager/move-files", this.moveFilesPayload)
        .then(() => {
          eventBus.$emit(
            "CREATE_NOTIFICATION",
            "Successfully moved the files.",
            "success",
            6500
          );
        });
    },
    // get all assign users
    async fetchAssignedUsers(fileId) {
      this.loadingAssignedUsers = true;
      await http()
        .get(
          `v3/file-manager/assigned-users/${
            fileId ? fileId : this.selectedFile.id
          }`
        )
        .then((response) => {
          this.assignedUsers = response.data.users;
          this.assignedCompanies = response.data.companies;
          this.assignedStores = response.data.stores;
        })
        .catch((error) => {
          useHandleErrorStatus(error);
        })
        .finally(() => {
          this.loadingAssignedUsers = false;
        });
    },
    // get all assign stores
    async fetchAssignedStores(fileId) {
      this.loadingAssignedUsers = true;
      await http()
        .get(
          `v3/file-manager/assigned-stores/${
            fileId ? fileId : this.selectedFile.id
          }`
        )
        .then((response) => {
          this.assignedStores = response.data.data;
        })
        .catch(() => {
          // useHandleErrorStatus(error);
        })
        .finally(() => {
          this.loadingAssignedUsers = false;
        });
    },
    // remove assigned user
    async removeAssingedUser(params) {
      await http()
        .post(`v3/file-manager/remove-assigned-user`, params)
        .then(() => {
          eventBus.$emit(
            "CREATE_NOTIFICATION",
            "Remove successfully",
            "success",
            6500
          );

          this.assignedUsers = this.assignedUsers.filter(
            (user) => user.id !== params.user_id
          );
        })
        .catch((error) => {
          useHandleErrorStatus(error);
        });
    },
    // remove assigned store
    async removeAssigned(params) {
      await http()
        .post(`v3/file-manager/remove-assigned`, params)
        .then(({ data }) => {
          eventBus.$emit(
            "CREATE_NOTIFICATION",
            "Remove successfully",
            "success",
            6500
          );
          this.selectedFile = data

          this.assignedUsers = this.assignedUsers.filter(
            (user) => user.id !== params.user_id
          );
        })
        .catch(() => {
          // useHandleErrorStatus(error);
        });
    },
    // update file name
    async renameFile(params, fileId) {
      this.loadingRename = true;
      await http()
        .post(`v3/file-manager/rename-file/${fileId}`, params)
        .then((response) => {
          this.selectedFile.name = response.data.data.name;
          this.files = this.files.map((file) => {
            if (file.id === response.data.data.id) {
              file = response.data.data;
            }
            return file;
          });

          eventBus.$emit(
            "CREATE_NOTIFICATION",
            "Rename successfully",
            "success",
            6500
          );
        })
        .catch((error) => {
          const { data } = useHandleErrorStatus(error);
          this.errorRename = data.errors;
        })
        .finally(() => {
          this.loadingRename = false;
        });
    },
    // download file
    async downloadFile(file) {
      await axios({
        url: file.full_path,
        method: "GET",
        responseType: "blob",
      })
        .then((response) => {
          const downloadUrl = window.URL.createObjectURL(
            new Blob([response.data])
          );
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.setAttribute("download", file.name);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
        })
        .catch((error) => {
          useHandleErrorStatus(error);
        });
    },
    // attach files to a units
    async attachToUnits() {
      this.isLoadingAttach = true;
      await http()
        .post("v3/file-manager/attach-to-units", this.attachToUnitsPayload)
        .then(() => {
          eventBus.$emit(
            "CREATE_NOTIFICATION",
            "Attached successfully",
            "success",
            6500
          );
        })
        .catch((error) => {
          const { data } = useHandleErrorStatus(error);
          this.errorAttachUnit = data.errors;
        })
        .finally(() => {
          this.isLoadingAttach = false;
        });
    },
    // get all related units
    async getUnitsRelatedLinks(fileId) {
      if (this.selectedFile && this.selectedFile.is_trash) {
        this.loadingUnitsRelatedLinks = false;
        return;
      }

      this.loadingUnitsRelatedLinks = true;
      await http()
        .get(
          `v3/file-manager/related-units/${
            fileId ? fileId : this.selectedFile.id
          }`
        )
        .catch(() => {
          this.isViewSelectedFile = false;
        })
        .then((response) => {
          this.unitsRelatedLinks = response.data;
        })
        .finally(() => {
          this.loadingUnitsRelatedLinks = false;
        });
    },
    async removeRelatedUnits(payload) {
      await http()
        .post("v3/file-manager/detach-to-files", payload)
        .then(() => {
          eventBus.$emit(
            "CREATE_NOTIFICATION",
            "Detached successfully",
            "success",
            6500
          );
        })
        .catch((error) => {
          useHandleErrorStatus(error);
        });
    },
    // delete files
    async deleteFiles(fileId) {
      this.loadingDelete = true;
      const payload = fileId ? { fileIds: fileId } : null;
      await http()
        .post(
          `v3/file-manager/delete`,
          payload ? payload : this.deletedFilesPayload
        )
        .then(() => {
          eventBus.$emit(
            "CREATE_NOTIFICATION",
            "Moved successfully",
            "success",
            6500
          );
        })
        .catch((error) => {
          useHandleErrorStatus(error);
        })
        .finally(() => {
          this.loadingDelete = false;
        });
    },
    async restoreFile(fileId) {
      const params = {
        file_ids: fileId ? [fileId] : this.restoreFilesPayload,
      };

      await http()
        .get(`v3/file-manager/restore`, { params })
        .then(() => {
          eventBus.$emit(
            "CREATE_NOTIFICATION",
            "Restored successfully",
            "success",
            6500
          );
          this.selectedTrash = [];
          this.isMultipleSelect = false;
        })
        .catch((error) => {
          useHandleErrorStatus(error);
        });
    },
    async permanentDelete(fileId) {
      const params = {
        file_ids: fileId ? [fileId] : this.restoreFilesPayload,
      };
      this.loadingDelete = true;
      await http()
        .get(`v3/file-manager/permanent-delete`, { params })
        .then(() => {
          eventBus.$emit(
            "CREATE_NOTIFICATION",
            "Deleted successfully",
            "success",
            6500
          );
          this.selectedTrash = [];
          this.isMultipleSelect = false;
        })
        .catch((error) => {
          useHandleErrorStatus(error);
        })
        .finally(() => {
          this.loadingDelete = false;
        });
    },
    async emptyTrash() {
      await http()
        .get("v3/file-manager/empty-trash")
        .then(() => {
          eventBus.$emit(
            "CREATE_NOTIFICATION",
            "Empty trash successfully",
            "success",
            6500
          );
          this.selectedTrash = [];
          this.isMultipleSelect = false;
        })
        .catch((error) => {
          useHandleErrorStatus(error);
        });
    },
    async restoreAllFiles() {
      await http()
        .get("v3/file-manager/restore-all")
        .then(() => {
          eventBus.$emit(
            "CREATE_NOTIFICATION",
            "Restore all files successfully",
            "success",
            6500
          );
          this.selectedTrash = [];
          this.isMultipleSelect = false;
        })
        .catch((error) => {
          useHandleErrorStatus(error);
        });
    },
    async duplicateFile(fileId) {
      this.loadingDuplicate = true;
      await http()
        .get(`v3/file-manager/duplicate/${fileId}`)
        .then((response) => {
          this.selectedFile = response.data.data;
          eventBus.$emit(
            "CREATE_NOTIFICATION",
            "Duplicate successfully",
            "success",
            6500
          );
        })
        .catch((error) => {
          useHandleErrorStatus(error);
        })
        .finally(() => {
          this.loadingDuplicate = false;
        });
    },
  },
});